import {
  MentalReadiness,
  CardioReadiness,
  MuscularReadiness,
  StrainData,
  PerformanceTestResult
} from '../types';

// ============================================
// HELPER FUNCTIONS FOR DATA GENERATION
// ============================================

// Generate dates backwards from today
function generateDateRange(endDate: string, days: number): string[] {
  const dates: string[] = [];
  const end = new Date(endDate);
  
  for (let i = 0; i < days; i++) {
    const date = new Date(end);
    date.setDate(end.getDate() - i);
    dates.push(date.toISOString().split('T')[0]);
  }
  
  return dates;
}

// Seeded random for consistent generation
function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// ============================================
// GENERATE SEASON DATA (180 DAYS)
// ============================================

const today = '2026-02-28'; // Today is Feb 28, 2026
const seasonDates = generateDateRange(today, 180); // 6 months
const athleteIds = ['1', '2', '3', '4', '5', '6', '7', '8'];

// ============================================
// MENTAL READINESS DATA (PRE)
// ============================================

export const seasonMentalData: MentalReadiness[] = [];
let mentalIdCounter = 1;

athleteIds.forEach(athleteId => {
  seasonDates.forEach((date, index) => {
    const dateObj = new Date(date);
    const dayOfWeek = dateObj.getDay();
    
    // Skip some random days (90% completion rate)
    const skipChance = seededRandom(parseInt(athleteId) * 1000 + index);
    if (skipChance > 0.9) return;
    
    // Weekend = better sleep, less stress
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    const baseSleep = isWeekend ? 4.5 : 4;
    const baseStress = isWeekend ? 1.5 : 2.5;
    const baseMood = isWeekend ? 4.5 : 4;
    
    const sleep = Math.min(5, Math.max(1, Math.round(baseSleep + seededRandom(parseInt(athleteId) * 100 + index) * 1.5 - 0.5)));
    const stress = Math.min(5, Math.max(1, Math.round(baseStress + seededRandom(parseInt(athleteId) * 200 + index) * 1.5 - 0.5)));
    const mood = Math.min(5, Math.max(1, Math.round(baseMood + seededRandom(parseInt(athleteId) * 300 + index) * 1.5 - 0.5)));
    // Fatica coerente con lo stress, dolori muscolari inversamente correlati alla qualità del sonno (mock demo)
    const fatigue = stress;
    const doms = Math.min(5, Math.max(1, 6 - sleep));
    const sleepHours = Math.round((4 + sleep * 0.9) * 10) / 10;

    seasonMentalData.push({
      id: `m${mentalIdCounter++}`,
      athleteId,
      date,
      sleepQuality: sleep,
      stress,
      fatigue,
      doms,
      sleepHours,
      sleep,
      mood,
      enteredBy: seededRandom(index) > 0.9 ? 'coach' : 'athlete'
    });
  });
});

// ============================================
// MUSCULAR READINESS DATA (PRE)
// ============================================

export const seasonMuscularData: MuscularReadiness[] = [];
let muscularIdCounter = 1;

// Athlete baselines for CMJ
const cmjBaselines: Record<string, number> = {
  '1': 40, '2': 35, '3': 43, '4': 37,
  '5': 33, '6': 38, '7': 39, '8': 41
};

// RSI baselines (for Drop Jump)
const rsiBaselines: Record<string, number> = {
  '1': 2.10, '2': 1.55, '3': 2.35, '4': 1.80,
  '5': 1.45, '6': 1.90, '7': 2.00, '8': 2.15
};

athleteIds.forEach(athleteId => {
  const baseline = cmjBaselines[athleteId];
  const rsiBaseline = rsiBaselines[athleteId];
  
  seasonDates.forEach((date, index) => {
    const dateObj = new Date(date);
    const dayOfWeek = dateObj.getDay();
    
    // Skip some random days (90% completion rate)
    const skipChance = seededRandom(parseInt(athleteId) * 2000 + index);
    if (skipChance > 0.9) return;
    
    // Weekend = lower DOMS, better CMJ
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    // CMJ varies ±5% from baseline
    const cmjVariation = (seededRandom(parseInt(athleteId) * 400 + index) - 0.5) * baseline * 0.1;
    const cmj = parseFloat((baseline + cmjVariation).toFixed(1));
    
    // RSI varies ±10% from baseline (more variable than CMJ)
    const rsiVariation = (seededRandom(parseInt(athleteId) * 4000 + index) - 0.5) * rsiBaseline * 0.2;
    const rsi = parseFloat((rsiBaseline + rsiVariation + (isWeekend ? 0.1 : 0)).toFixed(2));
    
    // Drop Jump Height (slightly lower than CMJ, about 90%)
    const dropJumpHeight = parseFloat((cmj * 0.9).toFixed(1));
    
    // Contact Time for Drop Jump (in ms) - lower is better
    const dropJumpContactTime = Math.round(180 + (seededRandom(parseInt(athleteId) * 5000 + index) - 0.5) * 40);
    
    // DOMS: lower on weekends
    const baseDoms = isWeekend ? 1.5 : 2.5;
    const doms = Math.min(5, Math.max(1, Math.round(baseDoms + seededRandom(parseInt(athleteId) * 500 + index) * 1.5 - 0.5)));
    
    // ACWR for muscular (training load ratio)
    const baseAcwr = 1.0 + (seededRandom(parseInt(athleteId) * 600 + index) - 0.5) * 0.8;
    const acwr = parseFloat(Math.max(0.5, Math.min(2.0, baseAcwr)).toFixed(2));
    
    // Alternate between CMJ and Drop Jump (coach decides which test to use)
    const useCMJ = seededRandom(parseInt(athleteId) * 7000 + index) > 0.5;
    
    seasonMuscularData.push({
      id: `mu${muscularIdCounter++}`,
      athleteId,
      date,
      testType: useCMJ ? 'cmj' : 'dropjump',
      cmj: useCMJ ? cmj : null,
      dropJumpHeight: useCMJ ? null : dropJumpHeight,
      dropJumpContactTime: useCMJ ? null : dropJumpContactTime,
      rsi: useCMJ ? null : rsi, // RSI only calculated for Drop Jump
      doms,
      enteredBy: seededRandom(index) > 0.9 ? 'coach' : 'athlete'
    });
  });
});

// ============================================
// CARDIO READINESS DATA (PRE)
// ============================================

export const seasonCardioData: CardioReadiness[] = [];
let cardioIdCounter = 1;

// Athlete baselines
const hrvBaselines: Record<string, number> = {
  '1': 70, '2': 55, '3': 80, '4': 60,
  '5': 50, '6': 65, '7': 68, '8': 72
};

const hrBaselines: Record<string, number> = {
  '1': 50, '2': 60, '3': 48, '4': 55,
  '5': 65, '6': 52, '7': 53, '8': 51
};

athleteIds.forEach(athleteId => {
  const hrvBaseline = hrvBaselines[athleteId];
  const hrBaseline = hrBaselines[athleteId];
  
  seasonDates.forEach((date, index) => {
    const dateObj = new Date(date);
    const dayOfWeek = dateObj.getDay();
    
    // Skip some random days (90% completion rate)
    const skipChance = seededRandom(parseInt(athleteId) * 3000 + index);
    if (skipChance > 0.9) return;
    
    // Weekend = better recovery
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    // HRV varies ±20% from baseline (higher on weekends)
    const hrvVariation = (seededRandom(parseInt(athleteId) * 700 + index) - 0.5) * hrvBaseline * 0.4;
    const hrv = parseFloat((hrvBaseline + hrvVariation + (isWeekend ? 5 : 0)).toFixed(1));
    
    // Resting HR (lower is better, lower on weekends)
    const hrVariation = (seededRandom(parseInt(athleteId) * 800 + index) - 0.5) * hrBaseline * 0.2;
    const restingHR = Math.round(hrBaseline + hrVariation - (isWeekend ? 3 : 0));
    
    seasonCardioData.push({
      id: `c${cardioIdCounter++}`,
      athleteId,
      date,
      hrv,
      restingHR
    });
  });
});

// ============================================
// STRAIN DATA (POST)
// ============================================

export const seasonStrainData: StrainData[] = [];
let strainIdCounter = 1;

// Helper to calculate ACWR from historical data
function calculateACWR(recentValues: number[]): number {
  if (recentValues.length < 7) return 0;
  
  const last7 = recentValues.slice(0, Math.min(7, recentValues.length));
  const last28 = recentValues.slice(0, Math.min(28, recentValues.length));
  
  const acuteLoad = last7.reduce((sum, v) => sum + v, 0) / last7.length;
  const chronicLoad = last28.reduce((sum, v) => sum + v, 0) / last28.length;
  
  return chronicLoad > 0 ? parseFloat((acuteLoad / chronicLoad).toFixed(2)) : 1.0;
}

athleteIds.forEach(athleteId => {
  const athleteStrainHistory: number[] = [];
  
  // Process dates in reverse order (oldest first) for proper ACWR calculation
  const reversedDates = [...seasonDates].reverse();
  
  reversedDates.forEach((date, index) => {
    const dateObj = new Date(date);
    const dayOfWeek = dateObj.getDay();
    
    // Weekend = rest day (low/no load)
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    // Generate sRPE (session load)
    let sRPE: number;
    if (isWeekend) {
      // Weekend: 20% chance of light session
      const hasSession = seededRandom(parseInt(athleteId) * 900 + index) < 0.2;
      sRPE = hasSession ? Math.round(150 + seededRandom(parseInt(athleteId) * 1000 + index) * 100) : 0;
    } else {
      // Weekday: normal training (300-600 load)
      const baseLoad = 450;
      const variation = (seededRandom(parseInt(athleteId) * 1100 + index) - 0.5) * 200;
      sRPE = Math.round(baseLoad + variation);
    }
    
    // Add to history for ACWR calculation
    athleteStrainHistory.unshift(sRPE); // Add to beginning since we're going backwards
    
    // Calculate ACWR from historical data
    const acwr = calculateACWR(athleteStrainHistory);
    
    seasonStrainData.push({
      id: `s${strainIdCounter++}`,
      athleteId,
      date,
      sRPE,
      acwr
    });
  });
});

// ============================================
// PERFORMANCE TEST RESULTS (TESTING)
// ============================================

export const seasonPerformanceTests: PerformanceTestResult[] = [];
let testIdCounter = 1;

// Test every 14 days (bi-weekly testing)
const testingDates = seasonDates.filter((_, index) => index % 14 === 0);

// All test IDs from performanceTestData
const allTestIds = [
  // Forza (Strength)
  'test-1rm-bench', 'test-1rm-squat', 'test-1rm-deadlift', 'test-legpress', 
  'test-pullup', 'test-pushup', 'test-cmj', 'test-dropjump', 'test-dropjump-rsi',
  'test-plank', 'test-handgrip',
  
  // Velocità (Speed)
  'test-10m', 'test-20m', 'test-30m', 'test-40m',
  'test-flying10', 'test-5-10-5', 'test-illinois', 'test-t-test', 'test-505',
  
  // Resistenza (Endurance)
  'test-yoyo', 'test-vo2max', 'test-beep', 'test-cooper',
  'test-1500m', 'test-3000m', 'test-5000m', 'test-rast',
  
  // Body Composition
  'test-weight', 'test-bodyfat', 'test-leanmass', 'test-bmi',
  'test-waist', 'test-skinfold'
];

// Base values for each athlete and test (EXPANDED - all tests)
const athleteTestBaselines: Record<string, Record<string, number>> = {
  '1': {
    // Strength
    'test-1rm-bench': 85, 'test-1rm-squat': 130, 'test-1rm-deadlift': 150, 'test-legpress': 220,
    'test-pullup': 12, 'test-pushup': 45, 'test-cmj': 42, 'test-dropjump': 40,
    'test-dropjump-rsi': 2.10, 'test-plank': 60, 'test-handgrip': 45,
    // Speed
    'test-10m': 1.75, 'test-20m': 3.05, 'test-30m': 4.25, 'test-40m': 5.50,
    'test-flying10': 2.15, 'test-5-10-5': 4.85, 'test-illinois': 15.2, 'test-t-test': 9.8, 'test-505': 11.5,
    // Endurance
    'test-yoyo': 18.2, 'test-vo2max': 55, 'test-beep': 12.5, 'test-cooper': 3100,
    'test-1500m': 320, 'test-3000m': 720, 'test-5000m': 1600, 'test-rast': 28,
    // Body Composition
    'test-weight': 75, 'test-bodyfat': 12, 'test-leanmass': 66, 'test-bmi': 23.5,
    'test-waist': 78, 'test-skinfold': 65
  },
  '2': {
    // Strength
    'test-1rm-bench': 55, 'test-1rm-squat': 95, 'test-1rm-deadlift': 105, 'test-legpress': 160,
    'test-pullup': 8, 'test-pushup': 35, 'test-cmj': 36, 'test-dropjump': 34,
    'test-dropjump-rsi': 1.55, 'test-plank': 50, 'test-handgrip': 35,
    // Speed
    'test-10m': 1.88, 'test-20m': 3.25, 'test-30m': 4.55, 'test-40m': 5.80,
    'test-flying10': 2.35, 'test-5-10-5': 5.25, 'test-illinois': 16.5, 'test-t-test': 10.5, 'test-505': 12.8,
    // Endurance
    'test-yoyo': 16.5, 'test-vo2max': 50, 'test-beep': 11.2, 'test-cooper': 2800,
    'test-1500m': 360, 'test-3000m': 810, 'test-5000m': 1700, 'test-rast': 32,
    // Body Composition
    'test-weight': 62, 'test-bodyfat': 18, 'test-leanmass': 51, 'test-bmi': 21.8,
    'test-waist': 72, 'test-skinfold': 95
  },
  '3': {
    // Strength
    'test-1rm-bench': 95, 'test-1rm-squat': 145, 'test-1rm-deadlift': 165, 'test-legpress': 245,
    'test-pullup': 14, 'test-pushup': 50, 'test-cmj': 44, 'test-dropjump': 42,
    'test-dropjump-rsi': 2.35, 'test-plank': 65, 'test-handgrip': 50,
    // Speed
    'test-10m': 1.80, 'test-20m': 3.15, 'test-30m': 4.35, 'test-40m': 5.60,
    'test-flying10': 2.20, 'test-5-10-5': 4.95, 'test-illinois': 15.5, 'test-t-test': 10.0, 'test-505': 11.8,
    // Endurance
    'test-yoyo': 17.8, 'test-vo2max': 53, 'test-beep': 12.2, 'test-cooper': 3050,
    'test-1500m': 330, 'test-3000m': 740, 'test-5000m': 1650, 'test-rast': 29,
    // Body Composition
    'test-weight': 82, 'test-bodyfat': 14, 'test-leanmass': 70, 'test-bmi': 24.2,
    'test-waist': 82, 'test-skinfold': 75
  },
  '4': {
    // Strength
    'test-1rm-bench': 60, 'test-1rm-squat': 100, 'test-1rm-deadlift': 110, 'test-legpress': 170,
    'test-pullup': 9, 'test-pushup': 38, 'test-cmj': 38, 'test-dropjump': 36,
    'test-dropjump-rsi': 1.80, 'test-plank': 55, 'test-handgrip': 40,
    // Speed
    'test-10m': 1.82, 'test-20m': 3.18, 'test-30m': 4.45, 'test-40m': 5.70,
    'test-flying10': 2.28, 'test-5-10-5': 5.15, 'test-illinois': 16.2, 'test-t-test': 10.3, 'test-505': 12.5,
    // Endurance
    'test-yoyo': 17.2, 'test-vo2max': 51, 'test-beep': 11.8, 'test-cooper': 2950,
    'test-1500m': 345, 'test-3000m': 780, 'test-5000m': 1750, 'test-rast': 30,
    // Body Composition
    'test-weight': 65, 'test-bodyfat': 16, 'test-leanmass': 55, 'test-bmi': 22.5,
    'test-waist': 75, 'test-skinfold': 85
  },
  '5': {
    // Strength
    'test-1rm-bench': 70, 'test-1rm-squat': 110, 'test-1rm-deadlift': 125, 'test-legpress': 185,
    'test-pullup': 10, 'test-pushup': 40, 'test-cmj': 34, 'test-dropjump': 32,
    'test-dropjump-rsi': 1.45, 'test-plank': 50, 'test-handgrip': 38,
    // Speed
    'test-10m': 1.95, 'test-20m': 3.40, 'test-30m': 4.75, 'test-40m': 6.00,
    'test-flying10': 2.45, 'test-5-10-5': 5.45, 'test-illinois': 17.0, 'test-t-test': 11.0, 'test-505': 13.2,
    // Endurance
    'test-yoyo': 15.8, 'test-vo2max': 48, 'test-beep': 10.5, 'test-cooper': 2650,
    'test-1500m': 380, 'test-3000m': 850, 'test-5000m': 1800, 'test-rast': 34,
    // Body Composition
    'test-weight': 78, 'test-bodyfat': 20, 'test-leanmass': 62, 'test-bmi': 25.0,
    'test-waist': 85, 'test-skinfold': 110
  },
  '6': {
    // Strength
    'test-1rm-bench': 58, 'test-1rm-squat': 98, 'test-1rm-deadlift': 108, 'test-legpress': 165,
    'test-pullup': 8, 'test-pushup': 36, 'test-cmj': 37, 'test-dropjump': 35,
    'test-dropjump-rsi': 1.90, 'test-plank': 53, 'test-handgrip': 42,
    // Speed
    'test-10m': 1.85, 'test-20m': 3.22, 'test-30m': 4.50, 'test-40m': 5.75,
    'test-flying10': 2.32, 'test-5-10-5': 5.20, 'test-illinois': 16.8, 'test-t-test': 10.7, 'test-505': 12.9,
    // Endurance
    'test-yoyo': 16.8, 'test-vo2max': 52, 'test-beep': 11.5, 'test-cooper': 2850,
    'test-1500m': 355, 'test-3000m': 800, 'test-5000m': 1700, 'test-rast': 31,
    // Body Composition
    'test-weight': 64, 'test-bodyfat': 17, 'test-leanmass': 53, 'test-bmi': 22.0,
    'test-waist': 74, 'test-skinfold': 88
  },
  '7': {
    // Strength
    'test-1rm-bench': 88, 'test-1rm-squat': 135, 'test-1rm-deadlift': 155, 'test-legpress': 230,
    'test-pullup': 13, 'test-pushup': 48, 'test-cmj': 40, 'test-dropjump': 38,
    'test-dropjump-rsi': 2.00, 'test-plank': 60, 'test-handgrip': 45,
    // Speed
    'test-10m': 1.78, 'test-20m': 3.10, 'test-30m': 4.30, 'test-40m': 5.55,
    'test-flying10': 2.18, 'test-5-10-5': 4.90, 'test-illinois': 15.4, 'test-t-test': 9.9, 'test-505': 11.7,
    // Endurance
    'test-yoyo': 17.5, 'test-vo2max': 54, 'test-beep': 12.0, 'test-cooper': 3020,
    'test-1500m': 335, 'test-3000m': 750, 'test-5000m': 1650, 'test-rast': 29,
    // Body Composition
    'test-weight': 77, 'test-bodyfat': 13, 'test-leanmass': 67, 'test-bmi': 23.8,
    'test-waist': 80, 'test-skinfold': 70
  },
  '8': {
    // Strength
    'test-1rm-bench': 90, 'test-1rm-squat': 140, 'test-1rm-deadlift': 160, 'test-legpress': 240,
    'test-pullup': 15, 'test-pushup': 52, 'test-cmj': 43, 'test-dropjump': 41,
    'test-dropjump-rsi': 2.15, 'test-plank': 65, 'test-handgrip': 50,
    // Speed
    'test-10m': 1.72, 'test-20m': 3.02, 'test-30m': 4.20, 'test-40m': 5.45,
    'test-flying10': 2.12, 'test-5-10-5': 4.80, 'test-illinois': 15.0, 'test-t-test': 9.7, 'test-505': 11.3,
    // Endurance
    'test-yoyo': 18.5, 'test-vo2max': 56, 'test-beep': 12.8, 'test-cooper': 3150,
    'test-1500m': 315, 'test-3000m': 710, 'test-5000m': 1750, 'test-rast': 27,
    // Body Composition
    'test-weight': 73, 'test-bodyfat': 11, 'test-leanmass': 65, 'test-bmi': 23.2,
    'test-waist': 76, 'test-skinfold': 62
  }
};

athleteIds.forEach(athleteId => {
  const baselines = athleteTestBaselines[athleteId];
  
  testingDates.forEach((date, dateIndex) => {
    // Ensure all tests are performed at least once - cycle through all tests across sessions
    // Each session performs a rotating subset of ~10-12 tests
    const sessionTestGroup = dateIndex % 4; // 4 groups for better coverage
    
    allTestIds.forEach((testId, testIndex) => {
      // Ensure each test is performed every 4th session minimum
      const shouldPerformTest = (testIndex % 4) === sessionTestGroup;
      
      if (!shouldPerformTest) return;
      
      const baseline = baselines[testId];
      if (!baseline) return;
      
      // Progressive improvement over season (slight upward trend)
      const progressionFactor = 1 + (dateIndex * 0.01); // 1% improvement per test date
      
      // Random variation ±5%
      const variation = (seededRandom(parseInt(athleteId) * 1200 + dateIndex + testId.charCodeAt(0)) - 0.5) * baseline * 0.1;
      
      let value = baseline * progressionFactor + variation;
      
      // Round based on test type
      if (testId.includes('sprint') || testId.includes('agility')) {
        value = parseFloat(value.toFixed(2));
      } else if (testId.includes('body-fat') || testId.includes('bmi')) {
        value = parseFloat(value.toFixed(1));
      } else {
        value = Math.round(value);
      }
      
      seasonPerformanceTests.push({
        id: `pt${testIdCounter++}`,
        athleteId,
        testId,
        date,
        value
      });
    });
  });
});